// CommunicationSections.tsx — v12.10.9 (S42 fallback warning) · was v12.1.18.0
// Communication settings area (2 independent sections): Email (SMTP) · SMS testing.
// Email: the tenant's own outbound SMTP (override of the platform "Tenant Default SMTP"). Password is
// write-only (masked, never returned). "Email live" reflects the resolved config (tenant → tenant-default
// → platform) without exposing the default's values. SMS testing is gated on the tenant phone's SmsCapable
// flag (same source as AI Agent → Phone & Activation); Send-test actions are save-then-act.
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { saveErrorToast } from '../lib/saveError';   // v12.3.12.5 (#334)
import { toast } from '../lib/toast';
import { HelpTooltip } from '../components/aiagent/HelpTooltip';
import './communication.css';

interface EmailData {
  host: string; port: number; username: string; hasPassword: boolean;
  fromAddress: string; fromName: string; replyTo: string; tlsMode: string;
  tenantConfigured: boolean; emailLive: boolean; emailSource: string;
  fallback?: { at?: string | null; host?: string | null; reason?: string | null } | null;   // v12.10.9 (S42)
}
interface SmsData { supported: boolean; fromNumber?: string | null; }
interface CommunicationData { email: EmailData; sms: SmsData; }

type EmailDraft = { host: string; port: number; username: string; fromAddress: string; fromName: string; replyTo: string; tlsMode: string };

export type CommunicationSectionKey = 'email';
export type CommunicationDirtyMap = Record<CommunicationSectionKey, boolean>;
export interface CommunicationState { dirty: CommunicationDirtyMap; anyDirty: boolean; saveAll: () => void; discardAll: () => void; }

const QKEY = ['t2-communication'];
const TLS: [string, string][] = [['starttls', 'STARTTLS (587)'], ['ssl', 'SSL/TLS (465)'], ['none', 'None (25)']];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toDraft(e: EmailData): EmailDraft {
  return { host: e.host, port: e.port, username: e.username, fromAddress: e.fromAddress, fromName: e.fromName, replyTo: e.replyTo, tlsMode: e.tlsMode };
}

export default function CommunicationSections({ onStateChange }: { onStateChange?: (s: CommunicationState) => void } = {}) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<CommunicationData>({
    queryKey: QKEY,
    queryFn: () => api.get<CommunicationData>('/t2/settings/communication'),
  });

  const [saved, setSaved] = useState<EmailDraft | null>(null);
  const [draft, setDraft] = useState<EmailDraft | null>(null);
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [live, setLive] = useState<{ emailLive: boolean; emailSource: string }>({ emailLive: false, emailSource: 'none' });
  const [emailTestTo, setEmailTestTo] = useState('');
  const [smsTestTo, setSmsTestTo] = useState('');

  useEffect(() => {
    if (!data) return;
    setSaved(toDraft(data.email));
    setDraft(toDraft(data.email));
    setHasPassword(data.email.hasPassword);
    setLive({ emailLive: data.email.emailLive, emailSource: data.email.emailSource });
    setPw('');
  }, [data]);

  const saveEmail = useMutation({
    mutationFn: (d: EmailDraft) => api.put<{ saved: boolean; emailLive: boolean; emailSource: string }>('/t2/settings/communication/email', {
      ...d, password: pw.length > 0 ? pw : undefined,
    }),
    onSuccess: (r) => {
      setSaved(draft ? structuredClone(draft) : saved);
      if (pw.length > 0) setHasPassword(true);
      setPw('');
      setLive({ emailLive: r.emailLive, emailSource: r.emailSource });
      queryClient.invalidateQueries({ queryKey: QKEY });
      toast('success', 'Email settings saved', r.emailLive ? 'Outbound email is live.' : 'Saved — email is not configured yet.');
    },
    onError: (err) => saveErrorToast(err, 'email settings'),   // v12.3.12.5 (#334)
  });

  const testEmail = useMutation({
    mutationFn: (to: string) => api.post<{ ok: boolean; detail: string }>('/t2/settings/communication/email/test', { to }),
    onSuccess: (r) => r.ok ? toast('success', 'Test email sent', r.detail) : toast('error', 'Test email failed', r.detail),
    onError: () => toast('error', 'Test email failed'),
  });
  const testSms = useMutation({
    mutationFn: (to: string) => api.post<{ ok: boolean; detail: string }>('/t2/settings/communication/sms/test', { to }),
    onSuccess: (r) => r.ok ? toast('success', 'Test text accepted', r.detail) : toast('error', 'Test text failed', r.detail),
    onError: () => toast('error', 'Test text failed'),
  });

  const reportRef = useRef(onStateChange);
  reportRef.current = onStateChange;

  const emailDirty = !!draft && !!saved && (JSON.stringify(draft) !== JSON.stringify(saved) || pw.length > 0);

  useEffect(() => {
    if (!reportRef.current) return;
    reportRef.current({
      dirty: { email: emailDirty },
      anyDirty: emailDirty,
      saveAll: async () => { try { if (emailDirty && draft) await saveEmail.mutateAsync(draft); } catch { /* toast surfaces */ } },
      discardAll: () => { setDraft(saved ? structuredClone(saved) : draft); setPw(''); },
    });
  }, [emailDirty, draft, saved]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !draft || !saved || !data) return <div className="settings-loading">Loading Communication settings…</div>;
  if (error) return <div className="settings-error">Communication settings could not be loaded.</div>;

  const patch = (p: Partial<EmailDraft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const fromInvalid = draft.fromAddress.length > 0 && !EMAIL_RE.test(draft.fromAddress);
  const replyInvalid = draft.replyTo.length > 0 && !EMAIL_RE.test(draft.replyTo);
  const emailTestValid = EMAIL_RE.test(emailTestTo);
  const smsSupported = data.sms.supported;

  return (
    <div className="organization-panel communication-panel">
      {/* ── 1. Email (SMTP) ────────────────────────────────────────────────── */}
      <Section title="Email (SMTP)" description="The outbound mail server used to send summaries, alerts, and caller communication on this organization's behalf."
        dirty={emailDirty} saving={saveEmail.isPending}
        onSave={() => saveEmail.mutate(draft)} onDiscard={() => { setDraft(structuredClone(saved)); setPw(''); }}
        canSave={!fromInvalid && !replyInvalid}>
        {data?.email.fallback ? (
          <div className="cm-fallback" role="alert" data-testid="email-fallback-warning">
            <b>Your email server refused our last attempt, so VoiceSpectrum sent for you.</b>{' '}
            {data.email.fallback.host ? <>Server {data.email.fallback.host}</> : <>Your server</>}
            {data.email.fallback.at ? <> at {new Date(data.email.fallback.at).toLocaleString()}</> : null}
            {data.email.fallback.reason ? <>: <i>{data.email.fallback.reason}</i></> : null}.
            {' '}Emails still go out from noreply@voicespectrum.ai with a note explaining why. Fix the settings below; the next good send clears this.
          </div>
        ) : null}
        <div className={`cm-status ${live.emailLive ? 'live' : 'off'}`}>
          <span className="dot" />
          {live.emailLive
            ? <span>Email communication is <b>live</b>{live.emailSource !== 'tenant' ? <span className="cm-status-src"> — using the platform default until you configure your own below</span> : null}.</span>
            : <span>Email is <b>not configured</b>. Enter your SMTP details below, or ask support to set a default.</span>}
        </div>

        <div className="fr two">
          <Field label="SMTP host" help={[['What it does', 'Outbound mail server hostname.'], ['Example', 'smtp.sendgrid.net']]}>
            <input value={draft.host} onChange={(e) => patch({ host: e.target.value })} placeholder="smtp.provider.com" />
          </Field>
          <Field label="Port" help={[['What it does', 'TCP port — 587 STARTTLS, 465 SSL, 25 plain.']]}>
            <input inputMode="numeric" value={draft.port} onChange={(e) => patch({ port: Number(e.target.value.replace(/[^0-9]/g, '')) || 0 })} />
          </Field>
        </div>
        <div className="fr two">
          <Field label="Username" help={[['What it does', 'The SMTP auth account.']]}>
            <input value={draft.username} onChange={(e) => patch({ username: e.target.value })} placeholder="apikey / user@domain" autoComplete="off" />
          </Field>
          <Field label="Password" help={[['What it does', 'The SMTP auth secret. Stored encrypted and never shown again.']]}>
            <div className="secret-row">
              <input className="secret-input" type={showPw ? 'text' : 'password'} value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder={hasPassword ? '•••••••• (saved — leave blank to keep)' : 'Enter password'} autoComplete="new-password" />
              <button type="button" className="secret-toggle" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? 'Hide' : 'Show'}>{showPw ? 'Hide' : 'Show'}</button>
            </div>
          </Field>
        </div>
        <div className="fr two">
          <Field label="From address" help={[['What it does', 'The "from" recipients see; must be on an authorized sending domain.']]}>
            <ValidatedInput value={draft.fromAddress} onChange={(v) => patch({ fromAddress: v })} invalid={fromInvalid} type="email" placeholder="noreply@yourclinic.com" />
          </Field>
          <Field label="From name" help={[['What it does', 'Display name shown alongside the from address.']]}>
            <input value={draft.fromName} onChange={(e) => patch({ fromName: e.target.value })} placeholder="Your Clinic" />
          </Field>
        </div>
        <div className="fr two">
          <Field label="Reply-to" help={[['What it does', 'Where replies route.']]}>
            <ValidatedInput value={draft.replyTo} onChange={(v) => patch({ replyTo: v })} invalid={replyInvalid} type="email" placeholder="frontdesk@yourclinic.com" />
          </Field>
          <Field label="Encryption" help={[['What it does', 'Connection encryption mode. Match your provider and port.']]}>
            <select value={draft.tlsMode} onChange={(e) => patch({ tlsMode: e.target.value })}>
              {TLS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>

        <div className="cm-testrow">
          <div className="f">
            <label>Send a test email to</label>
            <ValidatedInput value={emailTestTo} onChange={setEmailTestTo} invalid={emailTestTo.length > 0 && !emailTestValid} type="email" placeholder="you@yourclinic.com" />
          </div>
          <button type="button" className="btn-soft" disabled={!emailTestValid || testEmail.isPending} onClick={() => testEmail.mutate(emailTestTo)}>
            {testEmail.isPending ? 'Sending…' : 'Send test'}
          </button>
        </div>
        <p className="cm-hint">The test uses your <b>saved</b> settings. Save first, then send.</p>
      </Section>

      {/* ── 2. SMS testing ─────────────────────────────────────────────────── */}
      <Section title="SMS testing" description="Confirm text-message delivery for this organization. SMS capability itself is platform-managed."
        pointerOnly>
        {smsSupported ? (
          <>
            <div className="cm-status live"><span className="dot" /><span>Text messaging is <b>enabled</b> for {data.sms.fromNumber ?? 'your number'}.</span></div>
            <div className="cm-testrow" style={{ marginTop: 16 }}>
              <div className="f">
                <label>Send a test text to</label>
                <input value={smsTestTo} onChange={(e) => setSmsTestTo(e.target.value)} placeholder="+1 555 123 4567" inputMode="tel" />
              </div>
              <button type="button" className="btn-soft" disabled={smsTestTo.trim().length < 5 || testSms.isPending} onClick={() => testSms.mutate(smsTestTo.trim())}>
                {testSms.isPending ? 'Sending…' : 'Send test'}
              </button>
            </div>
          </>
        ) : (
          <div className="cm-amber">
            <WarnIcon />
            <div>
              <b>SMS is not enabled for this organization's phone number.</b>
              <p>Text-message capability comes from your provisioned phone number. Check <em>AI Agent → Phone &amp; Activation</em> to see your number's capabilities, or contact support to enable SMS.</p>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

// ── chrome ──
function Section(props: { title: string; description: string; dirty?: boolean; saving?: boolean; canSave?: boolean; onSave?: () => void; onDiscard?: () => void; pointerOnly?: boolean; children: ReactNode }) {
  return (
    <section className={`settings-section-card${props.dirty ? ' dirty' : ''}`}>
      <header className="settings-section-header">
        <div className="settings-section-headrow">
          <h2>{props.title}</h2>
          {!props.pointerOnly && (
            <div className="settings-actions">
              <button type="button" className="settings-secondary" disabled={!props.dirty || props.saving} onClick={props.onDiscard}>Discard</button>
              <button type="button" className="settings-primary" disabled={!props.dirty || props.saving || props.canSave === false} onClick={props.onSave}>{props.saving ? 'Saving…' : 'Save section'}</button>
            </div>
          )}
        </div>
        <p className="cm-sec-desc">{props.description}</p>
      </header>
      <div className="cm-section-body">{props.children}</div>
    </section>
  );
}

function Field({ label, help, children }: { label: string; help?: [string, ReactNode][]; children: ReactNode }) {
  return <div className="f"><label>{label}{help && <HelpTooltip sections={help} />}</label>{children}</div>;
}

function ValidatedInput({ value, onChange, invalid, type, placeholder }: { value: string; onChange: (v: string) => void; invalid: boolean; type?: string; placeholder?: string }) {
  return (
    <div className={`with-ic${invalid ? ' invalid' : ''}`}>
      <input type={type ?? 'text'} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {invalid && <span className="errmsg">Enter a valid email address.</span>}
    </div>
  );
}

function WarnIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>; }
